import { useState, useEffect, useRef } from 'react';
import Layout from '../Dashbaord/Layout.js/Layout';
import { FaTrash, FaEye, FaSearch, FaUpload, FaImages, FaTimes, FaCopy, FaExternalLinkAlt } from 'react-icons/fa';
import { colors } from '../../../utils/Colors';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Container, Row, Col, Button, Form, InputGroup, Spinner, Card, Modal } from 'react-bootstrap';
import galleryService from '../../../services/images.services';

const GalleryImages = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        try {
            setLoading(true);
            const response = await galleryService.getAllImages({
                search: searchTerm
            });
            
            setImages(response.data || []);
        } catch (error) {
            toast.error('Failed to load images');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter(file =>
            file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024
        );

        if (validFiles.length === 0) {
            toast.error('Please select valid image files (max 5MB each)');
            return;
        }

        setSelectedFiles(prev => [...prev, ...validFiles]);
    };

    const removeSelectedFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            toast.error('Please select images to upload');
            return;
        }

        setUploading(true);

        try {
            await galleryService.uploadImages(selectedFiles);
            toast.success('Images uploaded successfully');
            setSelectedFiles([]);
            setShowUploadModal(false);
            fetchImages();
        } catch (error) {
            toast.error('Failed to upload images');
        } finally {
            setUploading(false);
        }
    };

    const previewImage = (image) => {
        setSelectedImage(image);
        setShowPreviewModal(true);
    };

    const copyImageUrl = (url) => {
        navigator.clipboard.writeText(url);
        toast.success('Image URL copied');
    };

    const deleteImage = async () => {
        if (!imageToDelete) return;

        try {
            await galleryService.deleteSingleImage(imageToDelete._id);
            toast.success('Image deleted');
            setImages(prev => prev.filter(img => img._id !== imageToDelete._id));
            setDeleteModal(false);
            setImageToDelete(null);
        } catch (error) {
            toast.error('Failed to delete image');
        }
    };

    return (
        <Layout>
            <Container fluid className="py-4">
                {/* Header */}
                <Row className="mb-4 align-items-center p-4 rounded-3" style={{ backgroundColor: colors.TABLE_HEADERS }}>
                    <Col md={6}>
                        <h2 className="fw-bold mb-2 text-white">
                            <FaImages className="me-2" />
                            Image Gallery
                        </h2>
                    </Col>
                    <Col md={6} className="text-md-end">
                        <Button variant="light" onClick={() => setShowUploadModal(true)}>
                            <FaUpload /> Upload Images
                        </Button>
                    </Col>
                </Row>

                

                
                <Card>
                    <Card.Body>
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <p>Loading images...</p>
                            </div>
                        ) : images.length === 0 ? (
                            <div className="text-center py-5">
                                <p>No images found</p>
                                <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                                    Upload Images
                                </Button>
                            </div>
                        ) : (
                            <Row>
                                {images.map((image) => (
                                    <Col key={image._id} lg={3} md={4} sm={6} className="mb-3">
                                        <Card>
                                            <Card.Img
                                                variant="top"
                                                src={image.url}
                                                style={{ height: '200px', objectFit: 'cover', cursor: 'pointer' }}
                                                onClick={() => previewImage(image)}
                                            />
                                            <Card.Body className="text-center">
                                                <Button 
                                                    variant="danger" 
                                                    size="sm"
                                                    onClick={() => {
                                                        setImageToDelete(image);
                                                        setDeleteModal(true);
                                                    }}
                                                >
                                                    <FaTrash /> Delete
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </Card.Body>
                </Card>

                {/* Upload Modal */}
                <Modal show={showUploadModal} onHide={() => !uploading && setShowUploadModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Upload Images</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {uploading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p>Uploading...</p>
                            </div>
                        ) : (
                            <>
                                <div className="text-center mb-4">
                                    <div
                                        className="border rounded p-5"
                                        style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        <FaImages size={48} className="mb-3" />
                                        <p>Click to select images</p>
                                    </div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        multiple
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                    />
                                </div>

                                {selectedFiles.length > 0 && (
                                    <div className="mb-3">
                                        <p>Selected: {selectedFiles.length} files</p>
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                                <span>{file.name}</span>
                                                <Button size="sm" variant="outline-danger" onClick={() => removeSelectedFile(index)}>
                                                    <FaTimes />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowUploadModal(false)} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleUpload} disabled={uploading || selectedFiles.length === 0}>
                            {uploading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Preview Modal */}
                <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Image Preview</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedImage && (
                            <>
                                <div className="text-center mb-4">
                                    <img src={selectedImage.url} alt="Preview" style={{ maxWidth: '100%', maxHeight: '400px' }} />
                                </div>
                                <div className="d-grid gap-2">
                                    <Button variant="primary" onClick={() => copyImageUrl(selectedImage.url)}>
                                        <FaCopy /> Copy URL
                                    </Button>
                                    <Button variant="success" onClick={() => window.open(selectedImage.url, '_blank')}>
                                        <FaExternalLinkAlt /> Open in New Tab
                                    </Button>
                                </div>
                            </>
                        )}
                    </Modal.Body>
                </Modal>

                {/* Delete Modal */}
                <Modal show={deleteModal} onHide={() => setDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Image</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete this image?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={deleteImage}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </Layout>
    );
};

export default GalleryImages;